// The shared x axis for the two charts that plot one point per set.
//
// Points sit at their real position in time, scaled between the first and last set's written date.
// Sets are not written on a cadence — six annual ones, then a run of weekly ones, would be a lie
// told by even spacing, which draws the four-day gap and the two-year gap identically.
//
// Everything that follows exists because "one point per set" can mean six points or sixty:
//
//   * labels are thinned to whatever fits rather than truncated or rotated,
//   * marks shrink once they would overlap,
//   * hover bands are the midpoints between neighbours, so every point stays reachable even where
//     the marks are 3px apart.
//
// Sets with no written date can't be placed on a time scale at all, so the axis falls back to even
// slots for the whole chart — mixing the two would put a point somewhere it doesn't belong.

const EDGE_INSET = 16; // keeps the first and last label clear of the y-axis ticks and the frame
// Measured against 11px Inter rather than derived: digits and capitals, which is what these labels
// are made of, run wider than the font's average advance. Under-estimating here is what puts two
// ticks against each other, so this errs high.
const CHAR_W = 6.6;
const LABEL_GAP = 10;

export function timeAxis(points, left, width) {
  const times = points.map(p => (p.madeOn ? Date.parse(p.madeOn) : NaN));
  const dated = times.every(Number.isFinite) && new Set(times).size > 1;

  const x = dated
    ? scaleToTime(times, left, width)
    : points.map((_, i) => left + (width * (i + 0.5)) / points.length);

  return {
    x,
    dated,
    showLabel: fitLabels(points.map(p => p.label), x),
    bands: bands(x, left, width),
    // Past a couple of dozen points the marks stop being marks: at r=2.5 with a surface ring they
    // bead over the line they sit on and the series reads as a dotted rule. Drop them and let the
    // line carry the shape — the hover bands still give every point back one at a time.
    radius: points.length > 24 ? 0 : points.length > 14 ? 3.5 : 5
  };
}

function scaleToTime(times, left, width) {
  const min = Math.min(...times);
  const max = Math.max(...times);
  const inner = width - EDGE_INSET * 2;
  return times.map(t => left + EDGE_INSET + ((t - min) / (max - min)) * inner);
}

// Greedy left to right, then force the last tick: an axis whose final point is unlabelled reads as
// though the series stops early.
function fitLabels(labels, x) {
  const half = labels.map(label => (label.length * CHAR_W) / 2);
  const show = labels.map(() => false);
  const last = labels.length - 1;
  if (last < 0) return show;

  show[0] = true;
  let previous = 0;
  for (let i = 1; i <= last; i += 1) {
    if (x[i] - x[previous] >= half[i] + half[previous] + LABEL_GAP) {
      show[i] = true;
      previous = i;
    }
  }

  if (!show[last]) {
    show[last] = true;
    for (let i = last - 1; i > 0; i -= 1) {
      if (show[i] && x[last] - x[i] < half[last] + half[i] + LABEL_GAP) show[i] = false;
    }
  }
  return show;
}

// Hover targets tile the plot: each point owns the strip up to halfway to its neighbours.
function bands(x, left, width) {
  const right = left + width;
  return x.map((value, i) => {
    const start = i === 0 ? left : (x[i - 1] + value) / 2;
    const end = i === x.length - 1 ? right : (value + x[i + 1]) / 2;
    return { x: start, width: Math.max(1, end - start) };
  });
}
